import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Info, Lightbulb, Quote } from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import styles from './LessonContentRenderer.module.css';

interface CalloutProps {
    type: string;
    title: string;
    content: string;
}

const Callout: React.FC<CalloutProps> = ({ type, title, content }) => {
    const safeType = (type || 'info').toLowerCase();

    const getIcon = () => {
        switch (safeType) {
            case 'ohio':
                return (
                    <div className={styles.calloutLottieIcon}>
                        <DotLottieReact
                            src="/assets/animations/ohio-roadsign.lottie"
                            autoplay
                            loop
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            case 'danger':
                return (
                    <div className={styles.calloutLottieIcon}>
                        <DotLottieReact
                            src="/assets/animations/danger.lottie"
                            autoplay
                            loop
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            case 'critical':
            case 'important':
                return (
                    <div className={styles.calloutLottieIcon}>
                        <DotLottieReact
                            src="/assets/animations/critical.lottie"
                            autoplay
                            loop
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            case 'warning':
                return (
                    <div className={styles.calloutLottieIcon}>
                        <DotLottieReact
                            src="/assets/animations/warning-pulse.lottie"
                            autoplay
                            loop
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            case 'caution':
                return (
                    <div className={styles.calloutLottieIcon}>
                        <DotLottieReact
                            src="/assets/animations/danger.lottie"
                            autoplay
                            loop
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            case 'tip':
            case 'pro-tip':
                return (
                    <div className={styles.calloutLottieIcon}>
                        <DotLottieReact
                            src="/assets/animations/quick-tips.lottie"
                            autoplay
                            loop
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            case 'quote':
                return <Quote className={styles.calloutIcon} />;
            case 'reflection':
                return <img src="/assets/icons/brain.png" className={styles.calloutIcon} alt="Reflection" />;
            case 'takeaways':
                return <img src="/assets/icons/pencil.png" className={styles.calloutIcon} alt="Takeaways" />;
            case 'optional':
            case 'info':
            case 'note':
                return (
                    <div className={styles.calloutLottieIcon}>
                        <DotLottieReact
                            src="/assets/animations/information.lottie"
                            autoplay
                            loop
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            default:
                return <Info className={styles.calloutIcon} />;
        }
    };

    const variantClass = styles[`callout_${safeType.replace('-', '')}`] || styles.callout_info;

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className={`${styles.calloutContainer} ${variantClass}`}
        >
            <div className={styles.calloutHeader}>
                {getIcon()}
                <span className={styles.calloutTitle}>{title}</span>
            </div>
            <div className={styles.calloutContent} dangerouslySetInnerHTML={{ __html: content }} />
        </motion.div>

    );
};

export default Callout;
